import {
  Box,
  Button,
  FlexBox,
  Spinner,
  Tag,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { useEffect, useMemo } from 'react';

import {
  auditLogsApi,
  ConversationAutomationAuditLogEntry,
  AutomationToolExecutionData,
} from 'apis/auditLogs';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';

import styles from '../../activityLog.module.scss';
import { getRoutedNode } from '../routedThroughAutomation';

type AutomationTaskExecutionActivityProps = {
  readonly executionData: AutomationToolExecutionData;
};

export const AutomationTaskExecutionActivity = ({
  executionData: { execution_id: executionId },
}: AutomationTaskExecutionActivityProps): JSX.Element | null => {
  const {
    items: automationActivities,
    changeArg,
    asyncStatus,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: auditLogsApi.endpoints.readConversationAutomationAuditLogs,
    itemsSelector: (response) => response.data.logs,
    infinite: true,
  });

  useEffect(() => {
    if (executionId) {
      changeArg({
        resourceId: executionId,
        limit: 1000,
        sort: { key: 'timestamp', direction: 'asc' },
      });
    }
  }, [changeArg, executionId]);

  const integrationNode = automationActivities.find(
    (
      node,
    ): node is ConversationAutomationAuditLogEntry<'AUTOMATION_EXTERNAL_INTEGRATION_NODE_VISITED'> =>
      node.activity_type === 'AUTOMATION_EXTERNAL_INTEGRATION_NODE_VISITED',
  );
  const outcome = integrationNode?.outcome;
  const outcomeWasSuccess = outcome?.type === 'success';

  const outcomeText = useMemo(() => {
    if (!outcome) {
      return 'Status unknown';
    }

    if (outcomeWasSuccess) {
      return 'Ran successfully';
    }

    if (outcome?.type === 'timed_out') {
      return 'Failed to run (timeout)';
    }

    return 'Failed to run';
  }, [outcome, outcomeWasSuccess]);

  const outcomeColor = useMemo(() => {
    if (!outcome) {
      return colors.neutral[400];
    }

    if (outcomeWasSuccess) {
      return colors.green[400];
    }

    return colors.red[400];
  }, [outcome, outcomeWasSuccess]);

  if (!executionId) {
    return null;
  }

  return (
    <FlexBox gap="100" flexDirection="column">
      <Text.Paragraph variant="3"> Execution activity </Text.Paragraph>
      <FlexBox flexDirection="column" padding="0 200">
        {outcome && (
          <Tag color={outcomeColor} noBorder>
            {outcomeText}
          </Tag>
        )}
        <Box padding="200 200">
          {asyncStatus === 'rejected' && (
            <span className={styles.error}>
              Failed to retrieve automation activity log.
            </span>
          )}
          {automationActivities.map((node, index) => (
            <Text.Paragraph key={index} variant="3" subdued>
              {getRoutedNode(node)}
            </Text.Paragraph>
          ))}
          {asyncStatus === 'pending' && !automationActivities.length && (
            <div className={styles.spinnerContainer} data-testid="spinner">
              <Spinner />
            </div>
          )}
          {automationActivities.length > 0 && hasNextPage && (
            <FlexBox margin="200 0">
              <Button
                variant="secondary"
                isLoading={asyncStatus === 'pending'}
                onClick={nextPage}
              >
                Load more
              </Button>
            </FlexBox>
          )}
        </Box>
      </FlexBox>
    </FlexBox>
  );
};
